import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import Pagination from "@/components/base/Pagination";
import ComicFilter, { type ComicFilters } from "@/components/feature/ComicFilter";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { filterComics } from "@/lib/comicUtils";
import { comics } from "@/mocks/comics";

const PAGE_SIZE = 12;

export default function Category() {
  const params = useParams();
  const type = params.type ? decodeURIComponent(params.type) : "全部";
  const isAll = type === "全部";

  const [filters, setFilters] = useState<ComicFilters>({
    genre: isAll ? "全部" : type,
    region: "全部",
    status: "全部",
    sort: "updated",
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setFilters({
      genre: isAll ? "全部" : type,
      region: "全部",
      status: "全部",
      sort: "updated",
    });
    setPage(1);
  }, [type, isAll]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 280);
    return () => clearTimeout(timer);
  }, [filters, type]);

  const result = useMemo(
    () => filterComics({ ...filters, keyword: "" }),
    [filters]
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const pageItems = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const genreCounts = useMemo(() => {
    const map: Record<string, number> = {};
    (comics as { genres: string[] }[]).forEach((c) =>
      c.genres.forEach((g) => {
        map[g] = (map[g] || 0) + 1;
      })
    );
    return map;
  }, []);

  const pageTitle = isAll
    ? "全部漫画 - 歪歪漫画在线观看"
    : `${type}漫画 - 歪歪漫画在线观看`;

  useDocumentMeta({
    title: pageTitle,
    description: isAll
      ? "歪歪漫画在线观看全部分类漫画列表，涵盖热血、校园、恋爱、科幻、悬疑等题材，支持在线畅读。"
      : `${type}漫画分类列表，精选优质${type}题材漫画作品，支持在线阅读与多条件筛选，尽在歪歪漫画在线观看。`,
    keywords: `${type}漫画,${type}漫画在线观看,歪歪漫画,在线漫画`,
  });

  const updateFilters = (patch: Partial<ComicFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const resetFilters = () => {
    setFilters({
      genre: isAll ? "全部" : type,
      region: "全部",
      status: "全部",
      sort: "updated",
    });
  };

  const subTabs = [
    { value: "全部", label: "全部" },
    { value: "连载中", label: "连载中" },
    { value: "已完结", label: "已完结" },
  ];

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-6xl px-4 md:px-8 py-6">
        <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-700">{isAll ? "全部分类" : `${type}漫画`}</span>
        </nav>

        <div className="mb-5">
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">
            {isAll ? "全部漫画" : `${type}漫画`}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            {isAll
              ? "浏览平台全部漫画作品，可通过类型、地区、状态组合筛选。"
              : `本分类共收录 ${genreCounts[type] || 0} 部${type}题材作品，支持进一步筛选与排序。`}
          </p>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {subTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => updateFilters({ status: tab.value })}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                filters.status === tab.value
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 border border-background-300 text-foreground-600 hover:border-primary-400"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <ComicFilter value={filters} onChange={updateFilters} onReset={resetFilters} />
          <p className="hidden md:block text-sm text-foreground-500">
            共 <span className="text-primary-600 font-medium">{result.length}</span> 部作品
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="aspect-[5/7] w-full animate-pulse bg-background-200"></div>
                <div className="space-y-2 p-3">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-background-200"></div>
                  <div className="h-3 w-1/2 animate-pulse rounded bg-background-200"></div>
                </div>
              </div>
            ))}
          </div>
        ) : pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {pageItems.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-line text-3xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">暂无符合条件的漫画</p>
            <p className="mt-1 text-xs text-foreground-400">试试调整筛选条件，或重置后重新浏览</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}